import type { ImageUrlBuilderOptions } from '@sanity/image-url';
import { createImageUrlBuilder } from '@sanity/image-url';
import type { SanityImage } from '@time-line/components/types';
import { client } from 'lib/sanity';
import merge from 'lodash/merge';
import type { ImageProps } from 'next/image';
import type {
  ImageUrlBuilder as ImageUrlBuilderType,
  UseNextSanityImageBuilderOptions,
} from 'next-sanity-image';
import { useNextSanityImage } from 'next-sanity-image';

import { getLqipDimensions } from './getLqipDimensions';

const builder = createImageUrlBuilder(client);

export function urlFor(source: SanityImage) {
  return builder.image(source);
}

const imgOptions = (
  transform: ImageUrlBuilderType,
  options: UseNextSanityImageBuilderOptions,
  ratio?: number,
) => {
  const width = options?.width || 0;

  return transform
    .width(width)
    .height(
      Math.round(
        width /
          (ratio ||
            options.croppedImageDimensions?.aspectRatio ||
            options.originalImageDimensions.aspectRatio),
      ),
    )
    .fit('crop')
    .quality(80);
};

interface ImageOptions {
  config?: ImageUrlBuilderOptions;
  ratio?: number;
  fill?: boolean;
}

export const useImage = (
  image: SanityImage | null,
  { config, ratio, fill }: ImageOptions = {},
): ImageProps | undefined => {
  const imageProps = useNextSanityImage(client, image, {
    imageBuilder: (transform, options) => {
      const initialConfig = imgOptions(transform, options, ratio);
      const newConf = { options: config };
      return config ? merge(initialConfig, newConf) : initialConfig;
    },
  });

  if (!imageProps || !image) return;

  const [lqipWidth, lqipHeight] = getLqipDimensions(ratio);

  const lqip = urlFor(image)
    .width(lqipWidth)
    .height(lqipHeight)
    .fit('crop')
    .url();

  if (fill) {
    // Omit height and width properties if fill is true
    const { height, width, ...rest } = imageProps;

    return {
      ...rest,
      fill: true,
      alt: image.alt || '',
      blurDataURL: lqip,
      placeholder: lqip ? 'blur' : 'empty',
      suppressHydrationWarning: true,
    };
  }

  return {
    ...imageProps,
    alt: image.alt || '',
    height: ratio ? (imageProps.width || 0) / ratio : imageProps.height,
    blurDataURL: lqip,
    placeholder: lqip ? 'blur' : 'empty',
    suppressHydrationWarning: true,
  };
};
